/**
 * Granular React Query hooks for idea-related data
 * Each hook has a single responsibility for clean separation
 */

import { useQuery, useInfiniteQuery, useMutation, useQueryClient, keepPreviousData } from "@tanstack/react-query";
import { useAuth0 } from "@auth0/auth0-react";
import { useMemo, useRef, useEffect } from "react";
import {
  getIdeaById,
  updateIdea,
  toggleIdeaStar,
  getIdeaFilteredEvidences,
  getAssignedEvidences,
  getTabEvidenceTotals,
  getSuggestedEvidences,
  getSuggestedEvidenceIds,
  getAdditionalEvidences,
  getSubThemes,
  getEvidencesAggregations,
  getThemesBatchAggregations,
  getPerspectiveConfigurations,
  getImpactAggregationProperties,
  getAggregationKeysFromImpactConfig,
  getIdeaInfoTabsConfig,
  getIdeaColumns,
  getSubPages,
  getTabsConfig,
  calculateIdeaImpact,
  getPerspectiveCounts,
  getSinglePerspectiveCount,
  getProductStatusHistory,
} from "../../api/idea";
import { getThemes, getIdeaActivityLogs, getSubIdeas } from "../../Api";
import { EvidenceInclusion } from "../../components/ideas/store/types";
import { ideaKeys } from "./ideaKeys";
import { buildThemesBatchImpactKey } from "./themePerformanceHelpers";
import {
  mergeLazySuggestedIdsForMl,
  resolveIdeaThemesWithLazyIds,
  resolveRecalculatedThemesWithLazyIds,
} from "./themeLazyIdsHelpers";
import { getSuggestedPageLimit } from "./suggestedEvidencePagination";
import { ActiveFilter } from "../useFilters";
import {
  buildPerspectiveFiltersCacheKey,
  buildTabEvidenceTotalsCacheParams,
  cleanEmptyFilters,
  combineFilters,
} from "./filterHelpers";
import {
  resolvePerspectiveConfig,
  transformPerspectiveCountResponse,
} from "./perspective-count-utils";
// Helper function to normalize filter values for cache keys
const normalizeFilterValue = (selected: any) => {
  if (Array.isArray(selected)) {
    return [...selected].map(String).sort();
  }
  return selected;
};

// Helper function to create cache-safe filter representation
const createFilterCacheKey = (filter: ActiveFilter) => ({
  name: filter.name,
  dbFieldPath: filter.dbFieldPath,
  selected: normalizeFilterValue(filter.selected),
  operator: filter.operator,
  type: filter.type
});

// Individual query hooks - single responsibility each

export const useIdeaDetail = (ideaId?: string) => {
  const auth0 = useAuth0();
  const queryClient = useQueryClient();
  
  // Get cached detail data (from setQueryData in IdeasTable) and check if it exists
  const cachedDetailData = useMemo(() => {
    if (!ideaId) return undefined;
    return queryClient.getQueryData(ideaKeys.detail(ideaId));
  }, [ideaId, queryClient]);
  
  const hasCachedDetailData = !!cachedDetailData;
  
  return useQuery({
    queryKey: ideaKeys.detail(ideaId || ''),
    queryFn: () => getIdeaById(ideaId || '', auth0),
    enabled: !!ideaId,
    // Use cached detail data as initialData if available (marks it as fresh, won't refetch)
    initialData: cachedDetailData,
    // Fallback to list cache as placeholderData 
    placeholderData: () => {
      // If we have initialData, don't use placeholder
      if (hasCachedDetailData) return undefined;
      return ideaKeys.findIdeaInListCache(queryClient, ideaId || '');
    },
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
    // Always refetch on mount so direct URL /idea/:id loads get a full idea (e.g. aggregations)
    // even when a partial cache exists from the list view.
    refetchOnMount: hasCachedDetailData ? false : 'always',
  });
};

/** @deprecated Use useAssignedEvidences, useSuggestedEvidences, and useAdditionalEvidences instead. */
export const useFilteredEvidences = (
  ideaId?: string, 
  activeFilters?: ActiveFilter[],
  trendFilters?: ActiveFilter[],
  sortConfig?: { field: string; direction: 'asc' | 'desc' },
  searchQuery?: string,
  evidenceInclusion: EvidenceInclusion = 'total'
) => {
  const auth0 = useAuth0();
  
  console.log('🔍 [useFilteredEvidences] Called with:', {
    ideaId,
    activeFiltersCount: activeFilters?.length || 0,
    activeFilters,
    trendFiltersCount: trendFilters?.length || 0,
  });
  
  // Clean empty filters before processing
  const cleanActiveFilters = activeFilters ? cleanEmptyFilters(activeFilters) : [];
  const cleanTrendFilters = trendFilters ? cleanEmptyFilters(trendFilters) : [];
  
  // Combine both filter types for the API call
  const combinedFilters = [...cleanActiveFilters, ...cleanTrendFilters];
  
  // Clean parameters for cache key
  const cacheParams = {
    filters: cleanActiveFilters.map(createFilterCacheKey)
      .sort((a, b) => (a.name || '').localeCompare(b.name || '')),
    trendFilters: cleanTrendFilters.map(createFilterCacheKey)
      .sort((a, b) => (a.name || '').localeCompare(b.name || '')),
    sort: sortConfig || undefined,
    search: searchQuery || undefined,
    inclusion: evidenceInclusion,
  };
  
  return useQuery({
    queryKey: ideaKeys.filteredEvidences(ideaId || '', cacheParams),
    queryFn: async () => {
      /* console.log('🔍 Calling /evidences/filters_query API with combined filters:', combinedFilters?.length || 0);
      console.log('  - Business filters:', activeFilters?.length || 0);
      console.log('  - Trend filters:', trendFilters?.length || 0); */
      const result = await getIdeaFilteredEvidences(
        combinedFilters, 
        ideaId || '', 
        auth0, 
        searchQuery,
        sortConfig,
        evidenceInclusion
      );
      /* console.log('✅ /evidences/filters_query API completed'); */
      return result;
    },
    enabled: !!ideaId,
    staleTime: searchQuery ? 30 * 1000 : 10 * 60 * 1000, // Shorter stale time for searches
    refetchOnWindowFocus: false,
  });
};

const buildAssignedEvidencesCacheParams = (
  activeFilters?: ActiveFilter[],
  trendFilters?: ActiveFilter[],
  sortConfig?: { field: string; direction: 'asc' | 'desc' }
) => {
  const cleanActiveFilters = activeFilters ? cleanEmptyFilters(activeFilters) : [];
  const cleanTrendFilters = trendFilters ? cleanEmptyFilters(trendFilters) : [];

  return {
    cleanActiveFilters,
    cleanTrendFilters,
    cacheParams: {
      filters: cleanActiveFilters.map(createFilterCacheKey)
        .sort((a, b) => (a.name || '').localeCompare(b.name || '')),
      trendFilters: cleanTrendFilters.map(createFilterCacheKey)
        .sort((a, b) => (a.name || '').localeCompare(b.name || '')),
      sort: sortConfig || undefined,
    },
  };
};

/** @deprecated Use useAssignedEvidencesInfinite on the AI idea page. */
export const useAssignedEvidences = (
  ideaId?: string,
  activeFilters?: ActiveFilter[],
  trendFilters?: ActiveFilter[],
  sortConfig?: { field: string; direction: 'asc' | 'desc' }
) => {
  const auth0 = useAuth0();
  const { cleanActiveFilters, cleanTrendFilters, cacheParams } =
    buildAssignedEvidencesCacheParams(activeFilters, trendFilters, sortConfig);

  return useQuery({
    queryKey: ideaKeys.assignedEvidences(ideaId || '', cacheParams),
    queryFn: async () => {
      return await getAssignedEvidences(
        ideaId || '',
        cleanActiveFilters,
        cleanTrendFilters,
        auth0,
        sortConfig
      );
    },
    enabled: !!ideaId,
    staleTime: 10 * 60 * 1000,
    placeholderData: keepPreviousData,
    refetchOnWindowFocus: false,
  });
};

export const useAssignedEvidencesInfinite = (
  ideaId?: string,
  activeFilters?: ActiveFilter[],
  trendFilters?: ActiveFilter[],
  sortConfig?: { field: string; direction: 'asc' | 'desc' },
  evidenceInclusion: EvidenceInclusion = 'total'
) => {
  const auth0 = useAuth0();
  const { cleanActiveFilters, cleanTrendFilters, cacheParams } =
    buildAssignedEvidencesCacheParams(activeFilters, trendFilters, sortConfig);
  const isEnabled = !!ideaId && evidenceInclusion !== 'suggested';

  return useInfiniteQuery({
    queryKey: ideaKeys.assignedEvidencesInfinite(ideaId || '', cacheParams),
    queryFn: async ({ pageParam = 0 }) => {
      const skip = typeof pageParam === 'number' ? pageParam : 0;
      const limit = getSuggestedPageLimit(skip === 0);
      return getAssignedEvidences(
        ideaId || '',
        cleanActiveFilters,
        cleanTrendFilters,
        auth0,
        sortConfig,
        { limit, skip }
      );
    },
    initialPageParam: 0,
    getNextPageParam: (lastPage, allPages) => {
      if (lastPage?.hasMore === false) {
        return undefined;
      }
      const loadedCount = allPages.reduce(
        (sum, page) => sum + (page?.evidences?.length ?? 0),
        0
      );
      const totalCount = lastPage?.totalCount ?? allPages[0]?.totalCount;
      if (totalCount != null && loadedCount >= totalCount) {
        return undefined;
      }
      return loadedCount;
    },
    enabled: isEnabled,
    staleTime: 10 * 60 * 1000,
    placeholderData: keepPreviousData,
    refetchOnWindowFocus: false,
  });
};

export const useTabEvidenceTotals = (
  ideaId?: string,
  activeFilters?: ActiveFilter[],
  trendFilters?: ActiveFilter[],
  sortConfig?: { field?: string; direction?: 'asc' | 'desc' },
  evidenceInclusion: EvidenceInclusion = 'total'
) => {
  const auth0 = useAuth0();
  const resolvedSortConfig =
    sortConfig?.field && sortConfig?.direction
      ? { field: sortConfig.field, direction: sortConfig.direction }
      : undefined;
  const cacheParams = useMemo(
    () => buildTabEvidenceTotalsCacheParams(
      activeFilters || [],
      trendFilters || [],
      resolvedSortConfig,
      evidenceInclusion
    ),
    [activeFilters, trendFilters, resolvedSortConfig, evidenceInclusion]
  );
  const cleanActiveFilters = useMemo(
    () => cleanEmptyFilters(activeFilters || []),
    [activeFilters]
  );
  const cleanTrendFilters = useMemo(
    () => cleanEmptyFilters(trendFilters || []),
    [trendFilters]
  );

  return useQuery({
    queryKey: ideaKeys.tabEvidenceTotals(ideaId || '', cacheParams),
    queryFn: async () => {
      try {
        return await getTabEvidenceTotals(
          ideaId || '',
          cleanActiveFilters,
          cleanTrendFilters,
          auth0,
          evidenceInclusion,
          resolvedSortConfig
        );
      } catch {
        return { countsByTab: {} };
      }
    },
    enabled: !!ideaId,
    staleTime: 0,
    refetchOnWindowFocus: false,
    retry: false,
  });
};

/**
 * Smart polling intervals for suggested evidences
 * Increases dynamically to reduce unnecessary network calls
 */
const getSuggestedEvidencesPollingInterval = (fetchCount: number): number => {
  if (fetchCount <= 1) return 30000;   // 30 seconds for initial fetches
  if (fetchCount <= 3) return 60000;   // 1 minute after a few fetches
  if (fetchCount <= 6) return 120000;  // 2 minutes
  return 300000;                        // 5 minutes cap
};

export type SuggestedEvidencesInfiniteOptions = {
  sectionFilters?: ActiveFilter[];
  activeTab?: string;
  hasEmptyFilterIntersection?: boolean;
};

export const buildSuggestedEvidencesCacheParams = (
  activeFilters?: ActiveFilter[],
  trendFilters?: ActiveFilter[],
  sortConfig?: { field: string; direction: 'asc' | 'desc' },
  infiniteOptions?: SuggestedEvidencesInfiniteOptions
) => {
  const cleanActiveFilters = activeFilters ? cleanEmptyFilters(activeFilters) : [];
  const cleanTrendFilters = trendFilters ? cleanEmptyFilters(trendFilters) : [];
  const cleanSectionFilters = infiniteOptions?.sectionFilters
    ? cleanEmptyFilters(infiniteOptions.sectionFilters)
    : [];

  return {
    cleanActiveFilters,
    cleanTrendFilters,
    cacheParams: {
      filters: cleanActiveFilters.map(createFilterCacheKey)
        .sort((a, b) => (a.name || '').localeCompare(b.name || '')),
      trendFilters: cleanTrendFilters.map(createFilterCacheKey)
        .sort((a, b) => (a.name || '').localeCompare(b.name || '')),
      sectionFilters: cleanSectionFilters.map(createFilterCacheKey)
        .sort((a, b) => (a.dbFieldPath || a.name || '').localeCompare(b.dbFieldPath || b.name || '')),
      activeTab: infiniteOptions?.activeTab || undefined,
      sort: sortConfig || undefined,
    },
  };
};

const EMPTY_SUGGESTED_INFINITE_PAGE = {
  evidences: [],
  totalCount: 0,
  hasMore: false,
};

export const useSuggestedEvidencesInfinite = (
  ideaId?: string,
  activeFilters?: ActiveFilter[],
  trendFilters?: ActiveFilter[],
  sortConfig?: { field: string; direction: 'asc' | 'desc' },
  evidenceInclusion: EvidenceInclusion = 'total',
  infiniteOptions?: SuggestedEvidencesInfiniteOptions
) => {
  const auth0 = useAuth0();
  const { cleanActiveFilters, cleanTrendFilters, cacheParams } =
    buildSuggestedEvidencesCacheParams(activeFilters, trendFilters, sortConfig, infiniteOptions);
  const hasEmptyFilterIntersection = infiniteOptions?.hasEmptyFilterIntersection === true;
  const isEnabled = !!ideaId && evidenceInclusion === 'total' && !hasEmptyFilterIntersection;

  return useInfiniteQuery({
    queryKey: ideaKeys.suggestedEvidencesInfinite(ideaId || '', cacheParams),
    queryFn: async ({ pageParam = 0 }) => {
      if (hasEmptyFilterIntersection) {
        return EMPTY_SUGGESTED_INFINITE_PAGE;
      }

      const skip = typeof pageParam === 'number' ? pageParam : 0;
      const limit = getSuggestedPageLimit(skip === 0);
      return getSuggestedEvidences(
        ideaId || '',
        cleanActiveFilters,
        cleanTrendFilters,
        auth0,
        sortConfig,
        { limit, skip }
      );
    },
    initialPageParam: 0,
    getNextPageParam: (lastPage, allPages) => {
      if (hasEmptyFilterIntersection) {
        return undefined;
      }
      if (lastPage?.hasMore === false) {
        return undefined;
      }
      const loadedCount = allPages.reduce(
        (sum, page) => sum + (page?.evidences?.length ?? 0),
        0
      );
      const totalCount = lastPage?.totalCount ?? allPages[0]?.totalCount;
      if (totalCount != null && loadedCount >= totalCount) {
        return undefined;
      }
      return loadedCount;
    },
    enabled: isEnabled,
    staleTime: 10 * 60 * 1000,
    refetchOnWindowFocus: false,
  });
};

/** @deprecated Use useSuggestedEvidencesInfinite on the AI idea page. */
export const useSuggestedEvidences = (
  ideaId?: string,
  activeFilters?: ActiveFilter[],
  trendFilters?: ActiveFilter[],
  sortConfig?: { field: string; direction: 'asc' | 'desc' },
  evidenceInclusion: EvidenceInclusion = 'total'
) => {
  const auth0 = useAuth0();
  const { cleanActiveFilters, cleanTrendFilters, cacheParams } =
    buildSuggestedEvidencesCacheParams(activeFilters, trendFilters, sortConfig);
  const isEnabled = !!ideaId && evidenceInclusion === 'total';

  return useQuery({
    queryKey: ideaKeys.suggestedEvidences(ideaId || '', cacheParams),
    queryFn: async () => {
      return getSuggestedEvidences(
        ideaId || '',
        cleanActiveFilters,
        cleanTrendFilters,
        auth0,
        sortConfig
      );
    },
    enabled: isEnabled,
    refetchInterval: false,
    staleTime: 10 * 60 * 1000,
    placeholderData: keepPreviousData,
    refetchOnWindowFocus: false,
  });
};

export const useAdditionalEvidences = (
  ideaId?: string,
  activeFilters?: ActiveFilter[],
  trendFilters?: ActiveFilter[],
  searchQuery?: string,
  sortConfig?: { field: string; direction: 'asc' | 'desc' },
  evidenceInclusion: EvidenceInclusion = 'total'
) => {
  const auth0 = useAuth0();
  
  // Clean empty filters before processing
  const cleanActiveFilters = activeFilters ? cleanEmptyFilters(activeFilters) : [];
  const cleanTrendFilters = trendFilters ? cleanEmptyFilters(trendFilters) : [];
  
  // Only fetch when search query exists AND evidenceInclusion is 'total'
  const hasSearch = Boolean(searchQuery && searchQuery.trim().length >= 2);
  const isEnabled = Boolean(ideaId && hasSearch && evidenceInclusion === 'total');
  
  // Clean parameters for cache key
  const cacheParams = {
    filters: cleanActiveFilters.map(createFilterCacheKey)
      .sort((a, b) => (a.name || '').localeCompare(b.name || '')),
    trendFilters: cleanTrendFilters.map(createFilterCacheKey)
      .sort((a, b) => (a.name || '').localeCompare(b.name || '')),
    search: searchQuery || undefined,
    sort: sortConfig || undefined,
  };
  
  return useQuery({
    queryKey: ideaKeys.additionalEvidences(ideaId || '', cacheParams),
    queryFn: async () => {
      return await getAdditionalEvidences(
        ideaId || '',
        cleanActiveFilters,
        cleanTrendFilters,
        searchQuery || '',
        auth0,
        sortConfig
      );
    },
    enabled: isEnabled,
    staleTime: 30 * 1000,
    placeholderData: keepPreviousData,
    refetchOnWindowFocus: false,
  });
};

export const useIdeaTabs = () => {
  const auth0 = useAuth0();
  
  return useQuery({
    queryKey: ideaKeys.tabs(),
    queryFn: async () => {
      const response = await getTabsConfig(auth0);
      return response.tabs || [];
    },
    staleTime: 24 * 60 * 60 * 1000, // 24 hours - very long cache since config rarely changes
    gcTime: 7 * 24 * 60 * 60 * 1000, // 7 days - keep in cache for a week
    refetchOnWindowFocus: false,
    // Cache will automatically overwrite with fresh data on any refetch
  });
};

// Module-level cache to prevent duplicate calls across all instances
const fetchingThemesCache = new Map<string, Promise<any>>();
const mlFallbackCache = new Map<string, { attempted: boolean; hadFullInputs: boolean }>();

/**
 * Helper to extract evidence IDs from assigned and suggested evidences
 * Only called when ML API needs to be invoked (not on every render)
 */
export const extractEvidenceIdsForMLApi = (
  assignedEvidences?: any[],
  suggestedEvidences?: any[],
  filteredThemeEvidenceIds?: string[],
  ideaThemeEvidenceIds?: string[],
): string[] => {
  const assignedIds = assignedEvidences?.map((e: any) => e._id?.toString()).filter(Boolean) || [];
  // Only include "Strong" matches from suggested evidences
  const suggestedIds = suggestedEvidences
    ?.filter((e: any) => e.match?.level === "Strong")
    ?.map((e: any) => e._id?.toString())
    .filter(Boolean) || [];
  let ids = [...new Set([...suggestedIds, ...assignedIds])];
  // Exclude IDs in filteredThemeEvidenceIds (e.g. current theme's evidences for this recalc)
  if (filteredThemeEvidenceIds?.length) {
    const excludeSet = new Set(filteredThemeEvidenceIds.map((id) => String(id)));
    ids = ids.filter((id) => !excludeSet.has(id));
  }
  // Exclude idea.themeEvidenceIds (evidences already moved to sub-ideas)
  if (ideaThemeEvidenceIds?.length) {
    const excludeSet = new Set(ideaThemeEvidenceIds.map((id) => String(id)));
    ids = ids.filter((id) => !excludeSet.has(id));
  }
  return ids;
};

export const useSubIdeas = (ideaId?: string) => {
  const auth0 = useAuth0();

  return useQuery({
    queryKey: ideaKeys.subIdeas(ideaId || ''),
    queryFn: async () => {
      if (!ideaId) {
        return [];
      }
      const data = await getSubIdeas(ideaId, auth0);
      return Array.isArray(data) ? data : [];
    },
    enabled: !!ideaId,
    staleTime: 10 * 60 * 1000,
    refetchOnWindowFocus: false,
  });
};

export const useIdeaThemes = (
  ideaId?: string, 
  activeFilters?: string, 
  assignedEvidences?: any[], 
  recalculateThemes?: boolean,
  evidencesReady?: boolean,
  filteredThemeEvidenceIds?: string[],
  ideaThemeEvidenceIds?: string[],
  hasAnyEvidences?: boolean,
  subIdeasReady = true,
  activeFilterObjects?: any[]
) => {
  const auth0 = useAuth0();

  const shouldSkipThemesQuery =
    evidencesReady === true && hasAnyEvidences === false;

  // The DB /themes call (getThemes inside resolveIdeaThemesWithLazyIds) does NOT
  // need evidence data — only the ML fallback does. So we no longer gate the
  // whole query on evidencesReady; the query fires as soon as the idea + its
  // sub-ideas are known, so themes from the DB paint on page load instead of
  // waiting for (potentially many pages of) evidence to finish loading.
  const enabled =
    !!ideaId
    && !recalculateThemes
    && subIdeasReady
    && !shouldSkipThemesQuery;

  const mergeEvidenceIds = (suggestedIds: string[]) =>
    mergeLazySuggestedIdsForMl(
      suggestedIds,
      assignedEvidences,
      filteredThemeEvidenceIds || [],
      ideaThemeEvidenceIds || []
    );

  // Records whether the most recently completed fetch ran with BOTH inputs the
  // ML fallback needs (evidencesReady AND hasAnyEvidences). Used to decide if a
  // post-evidence refetch is needed for the ML fallback (see the effect below).
  // Tracking only evidencesReady is not enough: the first run can happen while
  // evidencesReady is already true but hasAnyEvidences is still false (evidence
  // counts populate a beat later), which would skip the ML fallback yet wrongly
  // mark it as "already attempted".
  const lastFetchHadFallbackInputsRef = useRef(false);

  const themesQuery = useQuery({
    queryKey: [...ideaKeys.themes(ideaId || '', activeFilters || '', recalculateThemes)],
    queryFn: async () => {
      if (!ideaId) {
        return { themes: { subThemes: [] } };
      }

      const ranWithFallbackInputs =
        evidencesReady === true && hasAnyEvidences === true;
      const fetchCacheKey = `${ideaId}-${activeFilters || ''}-${recalculateThemes ? 'recalc' : 'normal'}`;
      
      const existingFetch = fetchingThemesCache.get(fetchCacheKey);
      if (existingFetch) {
        return existingFetch;
      }
      
      const fetchPromise = resolveIdeaThemesWithLazyIds({
        getThemes: () => getThemes(ideaId, activeFilters || '', auth0),
        getSuggestedEvidenceIds: () =>
          getSuggestedEvidenceIds(ideaId, auth0, {
            strongMatchesOnly: true,
            activeFilters: activeFilterObjects || [],
          }),
        getSubThemes: (evidenceIds) =>
          getSubThemes(ideaId, auth0, evidenceIds, activeFilters || ''),
        mergeEvidenceIds,
        evidencesReady,
        hasAnyEvidences,
      }).finally(() => {
        lastFetchHadFallbackInputsRef.current = ranWithFallbackInputs;
        // Only update the shared cache when the queryFn actually ran the ML
        // fallback with full inputs. If evidences weren't ready yet, we must
        // NOT mark the identity as attempted — the refetch effect needs to
        // fire later once both evidencesReady and hasAnyEvidences are true.
        if (ranWithFallbackInputs) {
          const identity = `${ideaId}::${activeFilters || ''}`;
          mlFallbackCache.set(identity, { attempted: true, hadFullInputs: true });
        }
        fetchingThemesCache.delete(fetchCacheKey);
      });
      
      fetchingThemesCache.set(fetchCacheKey, fetchPromise);
      
      setTimeout(() => {
        fetchingThemesCache.delete(fetchCacheKey);
      }, 30000);
      
      return fetchPromise;
    },
    enabled,
    staleTime: recalculateThemes ? 0 : 10 * 60 * 1000,
    gcTime: recalculateThemes ? 0 : 30 * 60 * 1000,
    placeholderData: keepPreviousData,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    refetchOnReconnect: false,
  });

  const themesIdentity = `${ideaId || ''}::${activeFilters || ''}`;
  useEffect(() => {
    if (!enabled || !evidencesReady || !hasAnyEvidences) return;
    if (themesQuery.isFetching || themesQuery.data === undefined) return;
    // Per-instance guard: this instance's queryFn already ran with full inputs.
    if (lastFetchHadFallbackInputsRef.current) return;
    // Shared guard: ML already completed with full inputs across any instance.
    const sharedCache = mlFallbackCache.get(themesIdentity);
    if (sharedCache?.hadFullInputs) return;
    const dbReturnedThemes =
      (themesQuery.data.themes?.subThemes?.length ?? 0) > 0;
    if (dbReturnedThemes) return;
    // Shared guard: another instance already fired refetch() for this identity
    // (hadFullInputs will be set to true once that queryFn completes).
    if (sharedCache?.attempted) return;
    // Mark attempted immediately (synchronously) so concurrent instances
    // reading this in the same JS turn see it and skip their own refetch().
    mlFallbackCache.set(themesIdentity, { attempted: true, hadFullInputs: false });
    themesQuery.refetch();
  }, [
    enabled,
    evidencesReady,
    hasAnyEvidences,
    themesQuery.data,
    themesQuery.isFetching,
    themesIdentity,
  ]);

  return themesQuery;
};

/**
 * Separate hook for recalculating themes via ML API (getSubThemes).
 * Used when recalculateThemes is true; not part of useIdeaThemes.
 */
export const useRecalculateThemesQuery = (
  ideaId?: string,
  activeFilters?: string,
  recalculateThemes?: boolean,
  evidencesReady?: boolean,
  assignedEvidences?: any[],
  filteredThemeEvidenceIds?: string[],
  ideaThemeEvidenceIds?: string[],
  subIdeasReady = true,
  activeFilterObjects?: any[]
) => {
  const auth0 = useAuth0();
  const enabled =
    !!recalculateThemes &&
    !!ideaId &&
    evidencesReady === true &&
    subIdeasReady;

  const mergeEvidenceIds = (suggestedIds: string[]) =>
    mergeLazySuggestedIdsForMl(
      suggestedIds,
      assignedEvidences,
      filteredThemeEvidenceIds || [],
      ideaThemeEvidenceIds || []
    );

  return useQuery({
    queryKey: ideaKeys.recalculateThemes(ideaId || '', activeFilters || ''),
    queryFn: async () => {
      if (!ideaId) {
        return { themes: { subThemes: [] } };
      }
      return resolveRecalculatedThemesWithLazyIds({
        getSuggestedEvidenceIds: () =>
          getSuggestedEvidenceIds(ideaId, auth0, {
            strongMatchesOnly: true,
            activeFilters: activeFilterObjects || [],
          }),
        getSubThemes: (evidenceIds) =>
          getSubThemes(ideaId, auth0, evidenceIds, activeFilters || ''),
        mergeEvidenceIds,
      });
    },
    enabled,
    staleTime: 0,
    gcTime: 0,
    refetchOnWindowFocus: false,
  });
};

// Hook for fetching impact data for a single theme
export const useThemeImpact = (evidenceIds?: string[]) => {
  const auth0 = useAuth0();
  const { data: impactConfig, isSuccess: impactPropsReady } =
    useImpactAggregationProperties();
  const aggregationKeys = useMemo(
    () => getAggregationKeysFromImpactConfig(impactConfig),
    [impactConfig]
  );

  return useQuery({
    queryKey: ['theme-impact', evidenceIds?.sort() || [], aggregationKeys],
    queryFn: async () => {
      if (!evidenceIds || evidenceIds.length === 0) {
        return { aggregations: [] };
      }
      return await getEvidencesAggregations(evidenceIds, auth0, aggregationKeys);
    },
    enabled:
      !!evidenceIds && evidenceIds.length > 0 && impactPropsReady,
    staleTime: 10 * 60 * 1000, // 10 minutes
    refetchOnWindowFocus: false,
  });
};

/** How many themes are sent per get_aggregations call. Smaller chunks = faster
 *  first-paint for the most-evidence themes (rendered first) without waiting for
 *  all themes to finish. Chunks fire in parallel via Promise.all. */
const THEMES_IMPACT_CHUNK_SIZE = 3;

export const useThemesBatchImpacts = (themes: { id?: string; title?: string; evidenceIds?: string[] }[]) => {
  const auth0 = useAuth0();
  const { data: impactConfig, isSuccess: impactPropsReady } =
    useImpactAggregationProperties();
  const aggregationKeys = useMemo(
    () => getAggregationKeysFromImpactConfig(impactConfig),
    [impactConfig]
  );
  const evidenceIdLists = useMemo(
    () => themes.map((theme) => theme.evidenceIds || []),
    [themes]
  );
  const themesBatchKey = useMemo(() => buildThemesBatchImpactKey(themes), [themes]);
  const hasEvidenceLists = evidenceIdLists.some((list) => list.length > 0);

  return useQuery({
    queryKey: ['themes-batch-impact', themesBatchKey, aggregationKeys],
    queryFn: async () => {
      if (!hasEvidenceLists) {
        return { aggregations: themes.map(() => null) };
      }

      // Split evidence-id lists into chunks and call the API in parallel.
      // e.g. 8 themes → 3 parallel calls: [0-2], [3-5], [6-7]
      // First chunk resolves before later ones — ThemeCards with heavy evidence
      // lists show impact data sooner without blocking on the full set.
      const chunks: string[][][] = [];
      for (let i = 0; i < evidenceIdLists.length; i += THEMES_IMPACT_CHUNK_SIZE) {
        chunks.push(evidenceIdLists.slice(i, i + THEMES_IMPACT_CHUNK_SIZE));
      }

      const chunkResults = await Promise.all(
        chunks.map((chunk) =>
          getThemesBatchAggregations(chunk, auth0, aggregationKeys)
        )
      );

      // Flatten chunk results back into the original theme order
      const aggregations = chunkResults.flatMap((result, chunkIdx) =>
        chunks[chunkIdx].map((_, idx) => result?.aggregations?.[idx] ?? null)
      );

      return { aggregations };
    },
    enabled: themes.length > 0 && impactPropsReady && hasEvidenceLists,
    staleTime: 10 * 60 * 1000,
    refetchOnWindowFocus: false,
  });
};

export const useIdeaMLThemes = (ideaId?: string, evidenceIds?: string[], activeFilters?: string, shouldExecute?: boolean) => {
  const auth0 = useAuth0();
  const { data: impactConfig, isSuccess: impactPropsReady } =
    useImpactAggregationProperties();
  const aggregationKeys = useMemo(
    () => getAggregationKeysFromImpactConfig(impactConfig),
    [impactConfig]
  );

  const enabled =
    !!ideaId &&
    evidenceIds !== undefined &&
    evidenceIds.length > 0 &&
    !!shouldExecute &&
    impactPropsReady;

  return useQuery({
    queryKey: ideaKeys.MLthemes(ideaId || '', evidenceIds || [], activeFilters || '', shouldExecute),
    queryFn: async () => {
      /* console.log('ML Themes API Call EXECUTING - This should NOT appear when shouldExecute=false'); */
      if (!ideaId) return { themes: [] };
      try {
        const response_ML_API = await getSubThemes(ideaId, auth0, evidenceIds || [], activeFilters || '');
        // Create new themes array with aggregations instead of mutating the original
        const themesWithAggregations = await Promise.all(
          response_ML_API.subThemes.subThemes.map(async (subTheme: any) => {
            if(subTheme.evidenceIds.length > 0 && !subTheme.arr){
              const evidenceAggregations = await getEvidencesAggregations(
                subTheme.evidenceIds,
                auth0,
                aggregationKeys
              );
              // Return a new object with impact instead of mutating
              return {
                ...subTheme,
                impact: evidenceAggregations.aggregations[0]
              };
            }
            return subTheme;
          })
        );
        return { themes: themesWithAggregations || [] };
      } catch (mlError) {
        console.error('Error fetching ML themes:', mlError);
        return { themes: [] };
      }
    },
    enabled: enabled,
    staleTime: 0, // Never consider data stale - always refetch
    gcTime: 0, // Don't cache the result
    refetchOnWindowFocus: false,
  });
};

export const useGroupedAggregations = (evidenceIds?: string[]) => {
  const auth0 = useAuth0();
  const { data: impactConfig, isSuccess: impactPropsReady } =
    useImpactAggregationProperties();
  const aggregationKeys = useMemo(
    () => getAggregationKeysFromImpactConfig(impactConfig),
    [impactConfig]
  );
  const enabled =
    evidenceIds !== undefined &&
    evidenceIds.length > 0 &&
    impactPropsReady;

  return useQuery({
    queryKey: [...ideaKeys.evidencesAggregations(evidenceIds || []), aggregationKeys],
    queryFn: async () => {
      if (!evidenceIds || evidenceIds.length === 0) return { aggregations: null };
      try {
        const evidenceAggregations = await getEvidencesAggregations(
          evidenceIds,
          auth0,
          aggregationKeys
        );
        return evidenceAggregations;
      } catch (mlError) {
        console.error('Error fetching ML themes:', mlError);
        return { themes: [] };
      }
    },
    enabled: enabled,
    staleTime: 0, // Never consider data stale - always refetch
    gcTime: 0, // Don't cache the result
    refetchOnWindowFocus: false,
  });
};

// Mutations with optimistic updates
export const useUpdateIdea = (ideaId?: string) => {
  const queryClient = useQueryClient();
  const auth0 = useAuth0();
  
  return useMutation({
    mutationFn: (updatedIdea: any) => updateIdea(updatedIdea, auth0),
    onSuccess: (result, updatedIdea) => {
      const resolvedIdeaId = ideaId || updatedIdea._id || updatedIdea.id;
      if (!resolvedIdeaId || !result.success) return;

      queryClient.setQueryData(ideaKeys.detail(String(resolvedIdeaId)), (oldData: any) => {
        if (!oldData) return oldData;
        return {
          ...oldData,
          ...updatedIdea,
          updatedAt: new Date(),
        };
      });

      queryClient.invalidateQueries({
        queryKey: ideaKeys.details(),
        refetchType: 'none',
      });
    },
  });
};

export const useToggleIdeaStar = (ideaId?: string) => {
  const queryClient = useQueryClient();
  const auth0 = useAuth0();
  
  return useMutation({
    mutationFn: (starred: boolean) => toggleIdeaStar(ideaId || '', starred, auth0),
    onSuccess: (result) => {
      if (result.success) {
        queryClient.setQueryData(ideaKeys.detail(ideaId || ''), (oldData: any) => ({
          ...oldData,
          starred: [result.starred],
        }));
      }
    },
  });
};

// Configuration hooks - replacing SyncContext dependencies

export const usePerspectiveConfigurations = () => {
  const auth0 = useAuth0();
  
  return useQuery({
    queryKey: ideaKeys.config('perspectives'),
    queryFn: () => getPerspectiveConfigurations(auth0),
    staleTime: 10 * 60 * 1000, // 10 minutes
    refetchOnWindowFocus: false,
  });
};

export const useImpactAggregationProperties = () => {
  const auth0 = useAuth0();
  
  return useQuery({
    queryKey: ideaKeys.config('impactProperties'),
    queryFn: () => getImpactAggregationProperties(auth0),
    staleTime: 10 * 60 * 1000, // 10 minutes
    refetchOnWindowFocus: false,
  });
};

export const useIdeaInfoTabsConfig = () => {
  const auth0 = useAuth0();
  
  return useQuery({
    queryKey: ideaKeys.config('infoTabs'),
    queryFn: () => getIdeaInfoTabsConfig(auth0),
    staleTime: 10 * 60 * 1000, // 10 minutes
    refetchOnWindowFocus: false,
  });
};

export const useIdeaColumnsConfig = () => {
  const auth0 = useAuth0();
  
  return useQuery({
    queryKey: ideaKeys.config('columns'),
    queryFn: () => getIdeaColumns(auth0),
    staleTime: 10 * 60 * 1000, // 10 minutes
    refetchOnWindowFocus: false,
  });
};

export const useSubPagesConfig = () => {
  const auth0 = useAuth0();
  
  return useQuery({
    queryKey: ideaKeys.config('subPages'),
    queryFn: () => getSubPages(auth0),
    staleTime: 10 * 60 * 1000, // 10 minutes
    refetchOnWindowFocus: false,
  });
};

export const useTabsConfigNew = () => {
  const auth0 = useAuth0();
  
  return useQuery({
    queryKey: ideaKeys.config('tabs'),
    queryFn: () => getTabsConfig(auth0),
    staleTime: 10 * 60 * 1000, // 10 minutes
    refetchOnWindowFocus: false,
  });
};

// Impact calculation hook - independent of evidence queries
export const useIdeaImpactCalculation = (
  ideaId?: string,
  activeFilters?: ActiveFilter[],
  evidenceInclusion: EvidenceInclusion = 'total'
) => {
  const auth0 = useAuth0();
  
  // Clean empty filters before processing - memoize to prevent unnecessary recalculations
  const cleanActiveFilters = useMemo(() => {
    return activeFilters ? cleanEmptyFilters(activeFilters) : [];
  }, [activeFilters]);
  
  // Create cache params object - similar to useEvidencesTrend pattern
  // Include evidenceInclusion directly in the object so React Query detects changes
  const cacheParams = useMemo(() => {
    if (cleanActiveFilters.length === 0) {
      return {
        filters: '',
        inclusion: evidenceInclusion
      };
    }
    
    // Sort filters by name for consistent cache keys
    const sortedFilters = [...cleanActiveFilters].sort((a, b) => (a.name || '').localeCompare(b.name || ''));
    
    const filtersString = sortedFilters.map(f => {
      const selected = Array.isArray(f.selected) ? f.selected.sort().join(',') : f.selected;
      return `${f.name}:${selected}:${f.operator || 'eq'}`;
    }).join('|');
    
    return {
      filters: filtersString,
      inclusion: evidenceInclusion
    };
  }, [cleanActiveFilters, evidenceInclusion]);
  
  const hasFilters = cleanActiveFilters.length > 0;
  const isEnabled = !!ideaId && (hasFilters || evidenceInclusion !== 'total');
  
  // Create query key with evidenceInclusion as separate element for explicit change detection
  const queryKey = useMemo(() => {
    return [...ideaKeys.impact(ideaId || '', cacheParams), evidenceInclusion];
  }, [ideaId, cacheParams, evidenceInclusion]);
  
  /* console.log('🎯 useIdeaImpactCalculation - isEnabled:', isEnabled, 'filters:', cleanActiveFilters?.length || 0);
  console.log('🎯 useIdeaImpactCalculation - cacheParams:', cacheParams);
  console.log('🎯 useIdeaImpactCalculation - queryKey:', queryKey);
  console.log('🎯 useIdeaImpactCalculation - evidenceInclusion:', evidenceInclusion); */
  
  return useQuery({
    queryKey,
    queryFn: () => {
      // console.log('🚀 [useIdeaImpactCalculation] Calling API with:', {
      //   ideaId,
      //   filtersCount: cleanActiveFilters.length,
      //   evidenceInclusion
      // });
      return calculateIdeaImpact(ideaId || '', cleanActiveFilters, auth0, evidenceInclusion);
    },
    enabled: isEnabled, // Only call API when filters are applied
    staleTime: 1 * 60 * 1000, // 1 minute (shorter than evidence queries)
    refetchOnWindowFocus: false,
    // Make it independent - don't wait for other queries
    refetchOnMount: true,
    // Immediately trigger when filters change
    retry: 1,
  });
};

export const useSinglePerspectiveCountQuery = (
  ideaId: string | undefined,
  perspectiveKey: string | undefined,
  activeFilters: ActiveFilter[],
  trendFilters: ActiveFilter[],
  perspectiveConfigurations: any[],
  queryOptions?: { filtersCacheKey?: string; enabled?: boolean }
) => {
  const auth0 = useAuth0();
  const auth0Ref = useRef(auth0);
  auth0Ref.current = auth0;
  const filtersKey = useMemo(
    () => buildPerspectiveFiltersCacheKey(activeFilters, trendFilters),
    [activeFilters, trendFilters]
  );
  const filtersKeyForQuery = queryOptions?.filtersCacheKey ?? filtersKey;
  const allFilters = useMemo(
    () => combineFilters(activeFilters, trendFilters),
    [activeFilters, trendFilters]
  );
  const perspectiveConfigKeys = useMemo(
    () => perspectiveConfigurations.map((config: any) => config.key).sort().join(','),
    [perspectiveConfigurations]
  );

  return useQuery({
    queryKey: ideaKeys.singlePerspectiveCount(ideaId || '', {
      perspectiveKey: perspectiveKey || '',
      filtersKey: filtersKeyForQuery,
    }),
    queryFn: async () => {
      const perspectiveConfig = resolvePerspectiveConfig(
        perspectiveConfigurations,
        perspectiveKey || ''
      );
      if (!perspectiveConfig) {
        throw new Error(`Perspective configuration not found for key: ${perspectiveKey}`);
      }

      const fieldPath =
        perspectiveKey === 'trend' ? 'trend' : (perspectiveConfig.fieldPath || perspectiveKey);
      const trendRange = perspectiveKey === 'trend' ? 'All' : undefined;
      const response = await getSinglePerspectiveCount(
        ideaId || '',
        fieldPath || '',
        allFilters,
        trendRange,
        auth0Ref.current
      );

      return transformPerspectiveCountResponse(perspectiveKey || '', perspectiveConfig, response);
    },
    enabled:
      (queryOptions?.enabled ?? true) &&
      Boolean(ideaId) &&
      Boolean(perspectiveKey) &&
      Boolean(perspectiveConfigKeys) &&
      perspectiveConfigurations.length > 0,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    retry: false,
  });
};

export const usePerspectiveCounts = (
  ideaId?: string,
  currentPerspective?: string,
  activeFilters?: ActiveFilter[],
  perspectiveConfigurations?: any[]
) => {
  const auth0 = useAuth0();
  
  const cleanActiveFilters = activeFilters ? cleanEmptyFilters(activeFilters) : [];
  
  const cacheParams = {
    currentPerspective: currentPerspective || 'trend',
    filters: cleanActiveFilters.map(createFilterCacheKey).sort((a, b) => (a.name || '').localeCompare(b.name || '')),
    configs: perspectiveConfigurations?.map(c => c.key).sort() || []
  };
  
  return useQuery({
    queryKey: ideaKeys.perspectiveCounts(ideaId || '', cacheParams),
    queryFn: async () => {
      const result = await getPerspectiveCounts(
        ideaId || '',
        currentPerspective || 'trend',
        cleanActiveFilters,
        perspectiveConfigurations || [],
        auth0
      );
      
      return result;
    },
    enabled: !!ideaId && !!perspectiveConfigurations?.length,
    staleTime: 5 * 60 * 1000, // 5 minutes
    refetchOnWindowFocus: false,
  });
};

// Activity logs query
export const useIdeaActivityLogs = (ideaId?: string, enabled = true) => {
  const auth0 = useAuth0();
  
  return useQuery({
    queryKey: ideaKeys.activityLogs(ideaId || ''),
    queryFn: async () => {
      const response = await getIdeaActivityLogs(ideaId || '', auth0);
      return response.activityLogs || [];
    },
    enabled: !!ideaId && enabled,
    staleTime: 2 * 60 * 1000, // 2 minutes
    refetchOnWindowFocus: false,
  });
};

// Product status history query - for trend graph
export const useProductStatusHistory = (ideaId?: string) => {
  const auth0 = useAuth0();
  
  return useQuery({
    queryKey: ideaKeys.productStatusHistory(ideaId || ''),
    queryFn: async () => {
      const response = await getProductStatusHistory(ideaId || '', auth0);
      return response || [];
    },
    enabled: !!ideaId,
    staleTime: 5 * 60 * 1000, // 5 minutes
    refetchOnWindowFocus: false,
  });
};
